import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  getCustomerTypes,
  getAllCustomers,
  getSalesExecutives,
} from '../services/customer_service'
import {
  getDebtors,
  getTotalDebtors,
  getDebtorOrders,
} from '../services/debtor_service'
import { useAuth } from '../context/AuthContext'
import './Debtor.css'

const orderTypes = [
  { value: 'published_book', label: 'Publish Books' },
  { value: 'exercise_book', label: 'Exercise Books' },
]

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getOptionValue = (option) =>
  typeof option === 'object'
    ? getValue(option, ['id', 'value', 'customerid', 'customer_id', 'userid'])
    : option
const getOptionLabel = (option) =>
  typeof option === 'object'
    ? getValue(option, [
        'name',
        'label',
        'cust_name',
        'customer_name',
        'sales_exec_name',
        'sales_executive',
        'customertype',
        'type',
      ])
    : option
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)
const getDebtorCustomerId = (debtor) =>
  getValue(debtor, ['customerid', 'customer_id'])
const getDebtorOrderType = (debtor) => {
  const type = getValue(debtor, ['order_type', 'orderType'], '')
  const match = orderTypes.find((option) => option.value === type)
  return match ? match.label : type
}
const getDebtorAmountOwed = (debtor) =>
  getValue(debtor, ['amount_owed', 'amountOwed'], 0)
const getDebtorTotalOrder = (debtor) =>
  getValue(debtor, ['total_order', 'totalOrder'], 0)
const getDebtorTotalPaid = (debtor) =>
  getValue(debtor, ['total_paid', 'totalPaid'], 0)
const getDebtorOrdersOwed = (debtor) =>
  getValue(debtor, ['orders_owe', 'ordersOwe'], 0)
const getOrderId = (order) => getValue(order, ['orderid', 'order_id'])
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}

const baseFilters = {
  customer: '',
  customerType: '',
  orderType: '',
  salesExecutive: '',
}

function Debtor() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const loginUserId = user?.userid ?? user?.user_id ?? user?.id ?? ''
  const emptyFilters = baseFilters
  const [filters, setFilters] = useState(emptyFilters)
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters)
  const [isQueryOpen, setIsQueryOpen] = useState(false)
  const [debtors, setDebtors] = useState([])
  const [salesExecutives, setSalesExecutives] = useState([])
  const [customerTypes, setCustomerTypes] = useState([])
  const [customers, setCustomers] = useState([])
  const [totalDebtors, setTotalDebtors] = useState(0)
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [cursorHistory, setCursorHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [ordersModalDebtor, setOrdersModalDebtor] = useState(null)
  const [debtorOrders, setDebtorOrders] = useState([])
  const [isLoadingDebtorOrders, setIsLoadingDebtorOrders] = useState(false)
  const [debtorOrdersError, setDebtorOrdersError] = useState('')

  const loadDebtors = async (
    cursor = null,
    page = 1,
    query = appliedFilters,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getDebtors({ cursor, page, ...query })
      setDebtors(result.debtors)
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    Promise.all([
      getSalesExecutives(),
      getCustomerTypes(),
      getAllCustomers(),
      getTotalDebtors(),
    ])
      .then(
        ([
          loadedSalesExecutives,
          loadedCustomerTypes,
          loadedCustomers,
          loadedTotalDebtors,
        ]) => {
          setSalesExecutives(loadedSalesExecutives)
          setCustomerTypes(loadedCustomerTypes)
          setCustomers(loadedCustomers)
          setTotalDebtors(loadedTotalDebtors)

          const isLoginUserSalesExecutive = loadedSalesExecutives.some(
            (option) => String(getOptionValue(option)) === String(loginUserId),
          )
          if (loginUserId && isLoginUserSalesExecutive) {
            const defaultFilters = {
              ...baseFilters,
              salesExecutive: String(loginUserId),
            }
            setFilters(defaultFilters)
            setAppliedFilters(defaultFilters)
            loadDebtors(null, 1, defaultFilters)
          }
        },
      )
      .catch((requestError) => setError(requestError.message))
    loadDebtors(null, 1, emptyFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateFilter = (event) => {
    const { name, value } = event.target
    setFilters((current) => ({
      ...current,
      [name]: value,
      ...(name === 'customerType' ? { customer: '' } : {}),
    }))
  }
  const handleCustomerTypeChange = async (event) => {
    const customerType = event.target.value
    setFilters((current) => ({
      ...current,
      customerType,
      customer: '',
    }))
    setError('')

    try {
      setCustomers(await getAllCustomers(customerType))
    } catch (requestError) {
      setCustomers([])
      setError(requestError.message)
    }
  }
  const searchDebtors = (event) => {
    event.preventDefault()
    setAppliedFilters(filters)
    setCursorHistory([])
    loadDebtors(null, 1, filters)
  }
  const clearFilters = () => {
    setFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    setCursorHistory([])
    loadDebtors(null, 1, emptyFilters)
  }
  const goToNextPage = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadDebtors(nextCursor, nextPage || currentPage, filters)
  }
  const goToPreviousPage = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadDebtors(previous.cursor, previous.page, filters)
  }

  const viewDebtorOrders = async (debtor) => {
    setOrdersModalDebtor(debtor)
    setDebtorOrders([])
    setDebtorOrdersError('')
    setIsLoadingDebtorOrders(true)
    try {
      const orders = await getDebtorOrders({
        customer: getDebtorCustomerId(debtor),
        orderType: getValue(debtor, ['order_type', 'orderType'], ''),
      })
      setDebtorOrders(orders)
    } catch (requestError) {
      setDebtorOrdersError(requestError.message)
    } finally {
      setIsLoadingDebtorOrders(false)
    }
  }
  const closeDebtorOrders = () => setOrdersModalDebtor(null)
  const viewOrder = (orderId) => navigate(`/sales/orders/${orderId}`)

  return (
    <div className='debtors-page'>
      <nav className='debtors-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Debtors</strong>
      </nav>
      <header className='debtors-page__header'>
        <span className='app-content__eyebrow'>Sales</span>
        <h1>Debtors</h1>
        <p>
          Review outstanding customer balances and narrow the list by account.
        </p>
      </header>
      {error && (
        <div className='debtors-page__error' role='alert'>
          {error}
        </div>
      )}

      <section
        className='debtors-query-card'
        aria-labelledby='debtors-query-title'
      >
        <button
          className='debtors-query-card__toggle'
          type='button'
          onClick={() => setIsQueryOpen((open) => !open)}
          aria-expanded={isQueryOpen}
        >
          <span>
            <span className='app-content__eyebrow'>Refine results</span>
            <strong id='debtors-query-title'>Debtor query</strong>
          </span>
          <span aria-hidden='true'>{isQueryOpen ? '−' : '+'}</span>
        </button>
        {isQueryOpen && (
          <form className='debtors-query-form' onSubmit={searchDebtors}>
            <label>
              Customer type
              <select
                name='customerType'
                value={filters.customerType}
                onChange={handleCustomerTypeChange}
              >
                <option value=''>All customer types</option>
                {customerTypes.map((option) => (
                  <option
                    key={getOptionValue(option)}
                    value={getOptionValue(option)}
                  >
                    {getOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Customer
              <select
                name='customer'
                value={filters.customer}
                onChange={updateFilter}
              >
                <option value=''>All customers</option>
                {customers.map((customer) => (
                  <option
                    key={getValue(customer, ['customerid', 'id'])}
                    value={getValue(customer, ['customerid', 'id'])}
                  >
                    {getValue(customer, ['cust_name', 'name', 'company'])}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Order type
              <select
                name='orderType'
                value={filters.orderType}
                onChange={updateFilter}
              >
                <option value=''>All order types</option>
                {orderTypes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sales executive
              <select
                name='salesExecutive'
                value={filters.salesExecutive}
                onChange={updateFilter}
              >
                <option value=''>All sales executives</option>
                {salesExecutives.map((option) => (
                  <option
                    key={getOptionValue(option)}
                    value={getOptionValue(option)}
                  >
                    {getOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <div className='debtors-query-form__actions'>
              <button
                className='debtors-button debtors-button--primary'
                type='submit'
              >
                Search
              </button>
              <button
                className='debtors-button'
                type='button'
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </form>
        )}
      </section>

      <section className='debtors-summary-card' aria-label='Total owed'>
        <span className='app-content__eyebrow'>Outstanding</span>
        <span className='debtors-summary-card__label'>Total amount owed</span>
        <strong>{formatCurrency(totalDebtors)}</strong>
      </section>

      <section
        className='debtors-table-card'
        aria-labelledby='debtors-table-title'
      >
        <div className='debtors-table-card__header'>
          <div>
            <span className='app-content__eyebrow'>Debtor register</span>
            <h2 id='debtors-table-title'>Debtor list</h2>
            <p>{debtors.length} debtors on this page</p>
          </div>
        </div>
        <div className='debtors-table-wrap'>
          <table className='debtors-table'>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Order type</th>
                <th>Orders owed</th>
                <th>Total order</th>
                <th>Total paid</th>
                <th>Amount owed</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='debtors-table__empty' colSpan='7'>
                    Loading debtors...
                  </td>
                </tr>
              ) : debtors.length ? (
                debtors.map((debtor, index) => (
                  <tr key={`${getDebtorCustomerId(debtor)}-${index}`}>
                    <td>
                      <strong>
                        {getValue(debtor, ['cust_name', 'customer_name'])}
                      </strong>
                    </td>
                    <td>{getDebtorOrderType(debtor)}</td>
                    <td>{getDebtorOrdersOwed(debtor)}</td>
                    <td>{formatCurrency(getDebtorTotalOrder(debtor))}</td>
                    <td>{formatCurrency(getDebtorTotalPaid(debtor))}</td>
                    <td className='debtors-table__amount'>
                      {formatCurrency(getDebtorAmountOwed(debtor))}
                    </td>
                    <td>
                      <div className='debtors-actions'>
                        <button
                          type='button'
                          onClick={() => viewDebtorOrders(debtor)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='debtors-table__empty' colSpan='7'>
                    No debtors match the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='debtors-pagination'>
          <span>Showing up to 20 debtors</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousPage}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextPage}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>
      {ordersModalDebtor && (
        <div
          className='debtors-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeDebtorOrders()
          }
        >
          <section
            className='debtors-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='debtor-orders-title'
          >
            <div className='debtors-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Orders owed</span>
                <h2 id='debtor-orders-title'>
                  {getValue(ordersModalDebtor, ['cust_name', 'customer_name'])}
                </h2>
              </div>
              <button
                type='button'
                onClick={closeDebtorOrders}
                aria-label='Close orders owed'
              >
                x
              </button>
            </div>
            {debtorOrdersError && (
              <div className='debtors-page__error' role='alert'>
                {debtorOrdersError}
              </div>
            )}
            <div className='debtors-modal__list'>
              {isLoadingDebtorOrders ? (
                <p>Loading orders...</p>
              ) : debtorOrders.length ? (
                <table className='debtors-modal__table'>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Balance</th>
                      <th>
                        <span className='sr-only'>Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {debtorOrders.map((order) => (
                      <tr key={getOrderId(order)}>
                        <td>
                          <strong>{getOrderId(order)}</strong>
                        </td>
                        <td>{formatDate(getValue(order, ['request_date']))}</td>
                        <td>
                          {formatCurrency(getValue(order, ['balance'], 0))}
                        </td>
                        <td>
                          <button
                            type='button'
                            onClick={() => viewOrder(getOrderId(order))}
                          >
                            View order
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No outstanding orders found for this customer.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default Debtor
